import './EditModelWeightingModal.scss';
import { useState, useEffect, useCallback, useMemo, FC } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { Appearance, Design } from '@Components/designConstants';
import { MdClose, MdSave } from 'react-icons/md';
import { ModelConfig } from './hroas-types';
import balanceScaleImage from './balanceScale.png';

interface EditModelWeightingModalProps {
  show: boolean;
  onHide: () => void;
  models: ModelConfig[];
  onSave: (updatedModels: ModelConfig[]) => void;
  onPreview?: () => void;
  currentUserName?: string;
}

interface ModelWeightData {
  id: string;
  label: string;
  weight: number;
  reasoning: string;
  weightInputValue: string;
  lastUpdatedDate?: string;
  lastUpdatedBy?: string;
}

const formatDateShort = (date: Date): string =>
  `${date.getMonth() + 1}/${date.getDate()}/${String(date.getFullYear()).slice(-2)}`;

const formatLastUpdated = (date?: string, by?: string): string => {
  if (date && by) {
    return `Last updated on ${date} by ${by}`;
  }
  if (date) {
    return `Last updated on ${date}`;
  }
  return `Last updated by ${by}`;
};

const createInitialModelWeightData = (models: ModelConfig[]): ModelWeightData[] =>
  models.map(model => ({
    id: model.id,
    label: model.label,
    weight: model.weight,
    reasoning: model.reasoning || '',
    weightInputValue: model.weight.toFixed(2),
    lastUpdatedDate: model.lastUpdatedDate ?? formatDateShort(new Date()),
    lastUpdatedBy: model.lastUpdatedBy
  }));

export const EditModelWeightingModal: FC<EditModelWeightingModalProps> = ({
  show,
  onHide,
  models,
  onSave,
  onPreview: _onPreview,
  currentUserName
}) => {
  const initialWeightData = useMemo(() => createInitialModelWeightData(models), [models]);
  const [editedModels, setEditedModels] = useState<ModelWeightData[]>(initialWeightData);

  useEffect(() => {
    if (show) {
      setEditedModels(createInitialModelWeightData(models));
    }
  }, [show, models]);

  const handleWeightChange = useCallback((modelId: string, value: string) => {
    if (value === '') {
      setEditedModels(prev =>
        prev.map(model =>
          model.id === modelId ? { ...model, weightInputValue: '', weight: 0 } : model
        )
      );
      return;
    }

    const numValue = parseFloat(value);
    if (!Number.isNaN(numValue) && numValue >= 0) {
      setEditedModels(prev =>
        prev.map(model =>
          model.id === modelId ? { ...model, weight: numValue, weightInputValue: value } : model
        )
      );
    }
  }, []);

  const handleWeightBlur = useCallback((modelId: string) => {
    setEditedModels(prev =>
      prev.map(model => {
        if (model.id === modelId) {
          const numValue = parseFloat(model.weightInputValue);
          const validWeight = !Number.isNaN(numValue) && numValue >= 0 ? numValue : model.weight;
          return {
            ...model,
            weight: validWeight,
            weightInputValue: validWeight.toFixed(2)
          };
        }
        return model;
      })
    );
  }, []);

  const handleReasoningChange = useCallback((modelId: string, value: string) => {
    setEditedModels(prev =>
      prev.map(model => (model.id === modelId ? { ...model, reasoning: value } : model))
    );
  }, []);

  const handleSave = useCallback(() => {
    const todayFormatted = formatDateShort(new Date());
    const updatedModels: ModelConfig[] = models.map(model => {
      const edited = editedModels.find(e => e.id === model.id);
      if (!edited) {
        return model;
      }

      const numValue = parseFloat(edited.weightInputValue);
      const validWeight = !Number.isNaN(numValue) && numValue >= 0 ? numValue : edited.weight;

      return {
        ...model,
        weight: validWeight,
        reasoning: edited.reasoning || undefined,
        lastUpdatedDate: todayFormatted,
        lastUpdatedBy: currentUserName !== '' ? currentUserName : undefined
      };
    });

    onSave(updatedModels);
    onHide();
  }, [models, editedModels, onSave, onHide, currentUserName]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className='edit-model-weighting-modal'
      dialogClassName='edit-model-weighting-modal-dialog'
    >
      <Modal.Header className='edit-model-weighting-modal-header'>
        <div className='edit-model-weighting-modal-title-row'>
          <div className='edit-model-weighting-modal-title-left'>
            <img
              src={balanceScaleImage}
              alt='Balance scale'
              className='edit-model-weighting-modal-icon'
            />
            <Modal.Title className='edit-model-weighting-modal-title'>Model Weighting</Modal.Title>
          </div>
          <div className='edit-model-weighting-modal-title-right'>
            <button
              type='button'
              className='edit-model-weighting-modal-close-button'
              onClick={onHide}
            >
              <MdClose size={22} />
            </button>
          </div>
        </div>
        <p className='edit-model-weighting-modal-description'>
          Adjust each revenue stream&apos;s weight to reflect its real-world value or profit margin.
          <br />
          This ensures modeled results align with true business impact.
        </p>
      </Modal.Header>

      <Modal.Body className='edit-model-weighting-modal-body'>
        <div className='edit-model-weighting-modal-table-container'>
          <div className='edit-model-weighting-modal-table-header'>
            <div className='edit-model-weighting-modal-table-header-cell revenue-stream'>
              Revenue Stream
            </div>
            <div className='edit-model-weighting-modal-table-header-cell weight'>Weight</div>
            <div className='edit-model-weighting-modal-table-header-cell reasoning'>
              Reasoning (optional)
            </div>
          </div>

          <div className='edit-model-weighting-modal-table-rows'>
            {editedModels.map(model => (
              <div key={model.id} className='edit-model-weighting-modal-table-row'>
                <div className='edit-model-weighting-modal-table-row-content'>
                  <div className='edit-model-weighting-modal-table-row-cell revenue-stream'>
                    <p className='edit-model-weighting-modal-model-label'>{model.label}</p>
                  </div>
                  <div className='edit-model-weighting-modal-table-row-cell weight'>
                    <Form.Control
                      type='number'
                      step='0.1'
                      min='0'
                      value={model.weightInputValue}
                      onChange={e => handleWeightChange(model.id, e.target.value)}
                      onBlur={() => handleWeightBlur(model.id)}
                      className='edit-model-weighting-modal-weight-input'
                    />
                  </div>
                  <div className='edit-model-weighting-modal-table-row-cell reasoning'>
                    <Form.Control
                      type='text'
                      placeholder='Ex: Lower profit margin on Amazon'
                      value={model.reasoning}
                      onChange={e => handleReasoningChange(model.id, e.target.value)}
                      className='edit-model-weighting-modal-reasoning-input'
                    />
                  </div>
                </div>
                {(model.lastUpdatedDate || model.lastUpdatedBy) && (
                  <p className='edit-model-weighting-modal-last-updated'>
                    {formatLastUpdated(model.lastUpdatedDate, model.lastUpdatedBy)}
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className='edit-model-weighting-modal-tip'>
            <p className='edit-model-weighting-modal-tip-text'>
              Tip: Weights lower than 1.00 reduce how much that revenue stream influences your
              overall results.
            </p>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className='edit-model-weighting-modal-footer'>
        <div className='edit-model-weighting-modal-footer-left'>
          <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onHide}>
            Cancel
          </Button>
        </div>
        <div className='edit-model-weighting-modal-save-group'>
          <Button onClick={handleSave} leadingElement={<MdSave />}>
            Update Default Weights
          </Button>
          <p className='edit-model-weighting-modal-admin-only'>Admin-Only</p>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
